import React, { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useRef, useEffect } from 'react';
import TikTokAdsSSOLogin from '@byted/common-login-i18n-sdk';

export interface AdsLoginProps {
  ssoConfig: {
    appId: number; // 产品线申请的 appId
    width?: number; // SDK 默认宽度，默认值 320
    service: string; // 登录或改密成功后的回调地址
    host?: string; // sso 域名默认(美东/新加坡)：https://business-sso.tiktok.com/ 印度地区 sso 域名: https://in-business-sso.tiktok.com/ boei18n 环境搜搜域名: http://business-sso-boe.remove.this/
    openSSO?: boolean; // sso 单点登录能力，默认值 true
    signUp?: string | ((...args: any[]) => any); // 注册按钮点击后的跳转外链（新开页打开） or 点击回调函数
    lang?: string; // 多语言类型，默认值 'en'
    email?: string; // 初始化时可填充的一个默认的邮箱值，若不符合邮箱格式校验会触发前端提示
    account?: string; // 初始化时可填充的一个默认的邮箱或者手机号码值，若不符合账户格式校验会触发前端提示
    accountTypes?: ('mobile' | 'email')[]; // 账户类型，支持手机/邮箱，优先展示第一个，默认值 ['mobile', 'email']
    panelTypes?: ('password' | 'code')[]; // 面板类型，支持密码/验证码，默认值 ['password', 'code']
    codeAccountTypes?: ('mobile' | 'email')[]; // 验证码面板账户类型，支持手机/邮箱，默认值 ['mobile', 'email']
    className?: string;
    defaultArea?: string; // 例如：+86
    defaultCountry?: string; // 例如：CN
    showLabel?: boolean; // 是否显示 Label Text，默认值 true
    showTitle?: boolean; // 是否显示 title，默认值 true
    securityUrl?: string; // 风控阻塞登陆时的外链解释，查看更多，默认值 'https://ads.tiktok.com/help/article?aid=144746111208386133'
    captchaStyle?: any; // 可以复写滑块的前端样式，具体参数参见验证中心的文档：https://bytedance.feishu.cn/wiki/wikcnz9VUjYsNGeeMBgCoOy4XBe#QvQbfm
    enableLarkLogin?: boolean; // 启用内部员工 lark 扫码登录入口，默认 false
    loginInLarkDomain?: string; // 启用内部员工 lark 扫码登录入口的接口 domain，若不指定，默认是当前域名下
    ttwid?: boolean; // 开启双重验证时需要打开，web 设备识别使用
    tsv?: {
      contactUrl?: string; // 重置密码无可用的验证方式时可以引导用户到帮助页面，默认 isBOE ? '//boei18n-ads.byteoversea.net/i18n/feedback' : '//ads.tiktok.com/i18n/feedback'
      onSuccess?: (...args: any[]) => Promise<void>; // 双重验证验证成功后的回调函数，会阻塞正常流程，需要返回 Promise.resolve()
      onError?: (...args: any[]) => any; // 双重验证验证失败后的回调函数
      onNoAvaialbeVerifyWay?: (...args: any[]) => any; // 重置密码无可用的验证方式回调函数
    };
    switchMobileCodeOrPwLogin?: boolean; // 手机切换短信（仅登录），密码登录
    loginSuccess?: (...args: any[]) => any; // 登陆成功的回调函数；如果不传，登录成功后会 redirect 到 sso 登录接口返回的 redirect_url 上
    loginFail?: (...args: any[]) => any; // 登陆失败的回调函数
    resetPwdSuccess?: (...args: any[]) => any; // 改密成功的回调函数；改密成功自动回到登录面板
    resetPwdFail?: (...args: any[]) => any; // 改密失败的回调函数
    resetPwd?: (...args: any[]) => any; // 点击重置密码的回调函数
    loginIn?: (...args: any[]) => any; // 点击最下方登录的回调函数
  };
  id?: string;
  style?: React.CSSProperties;
  className?: string;
}

export interface AdsLoginRef {
  getInstance: () => any;
}

const LoginContainer: ForwardRefRenderFunction<AdsLoginRef, AdsLoginProps> = (props, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const ssoRef = useRef<any>(null);
  const { id = 'sso_login', style = {}, className = '' } = props;
  useImperativeHandle(ref, () => ({
    getInstance: () => ssoRef.current
  }));
  useEffect(() => {
    const root = containerRef.current;
    const instance = new TikTokAdsSSOLogin({
      ...props.ssoConfig,
      root
    });
    ssoRef.current = instance;
    instance.init();
  }, []);
  return <div ref={containerRef} id={id} style={style} className={className} />;
};
export const AdsLogin = forwardRef(LoginContainer);
export default AdsLogin;
